// ============================================================================
// Validation engine — runs declarative ValidationRule[] + required checks
// ============================================================================
// Two layers:
//   1. Per-field required: any field with `required: true` and an empty value
//      contributes a field-level error keyed by the field's `key`.
//   2. Cross-field rules from form_spec.validations: each `rule` expression is
//      evaluated against the current values. When false:
//        - severity='error'   → contributes to fieldErrors (keyed by the rule
//          id so the same rule shows once) AND prevents save.
//        - severity='warning' → shows as a non-blocking drift badge.
//
// Conventions mirror PRIMA's FSA drift model: warnings inform, errors block.

import {
  collectAllFields,
  type FieldSpec,
  type FormSpec,
  type FormValues,
  type ValidationRule,
} from 'api/types/formSpec.types';
import { evalBool } from './expressionEvaluator';

export interface ValidationResult {
  /** Field-level errors keyed by field key (or rule id for cross-field rules). */
  fieldErrors: Record<string, string>;
  /** Non-blocking warnings — drift-style. */
  warnings: Array<{ id: string; message: string }>;
  /** Convenience — `true` only when fieldErrors is empty. */
  canSave: boolean;
}

const isEmpty = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
};

/**
 * Walk the form_spec, evaluate required + show_if + validation rules against
 * the current values, return the combined error/warning bundle.
 *
 * show_if hides a field from validation when its condition is false — a
 * hidden required field is NOT enforced, matching user intuition.
 */
export function runValidations(spec: FormSpec, values: FormValues): ValidationResult {
  const fieldErrors: Record<string, string> = {};
  const warnings: ValidationResult['warnings'] = [];

  // 1. Required + per-field checks (visible fields only) — walks sections + flat fields
  for (const field of collectAllFields(spec)) {
    // Computed fields are derived — never user-input; skip required/type checks.
    if (field.computed) continue;
    if (field.show_if && !evalBool(field.show_if, values, /*defaultOnError*/ true)) continue;

    const v = values[field.key];

    if (field.required && isEmpty(v)) {
      fieldErrors[field.key] = `${field.label} is required`;
      continue;
    }

    // Type-specific length / range checks (best-effort; backend re-validates)
    switch (field.type) {
      case 'text': {
        const s = typeof v === 'string' ? v : '';
        if (field.max_length && s.length > field.max_length) {
          fieldErrors[field.key] = `${field.label} max ${field.max_length} characters`;
        } else if (field.min_length && s.length > 0 && s.length < field.min_length) {
          fieldErrors[field.key] = `${field.label} min ${field.min_length} characters`;
        } else if (field.pattern && s && !new RegExp(field.pattern).test(s)) {
          fieldErrors[field.key] = `${field.label} format is invalid`;
        }
        break;
      }
      case 'number':
      case 'currency': {
        if (!isEmpty(v)) {
          const n = Number(v);
          if (!Number.isFinite(n)) {
            fieldErrors[field.key] = `${field.label} must be a number`;
          } else if (field.min !== undefined && n < field.min) {
            fieldErrors[field.key] = `${field.label} must be at least ${field.min}`;
          } else if (field.max !== undefined && n > field.max) {
            fieldErrors[field.key] = `${field.label} must be at most ${field.max}`;
          }
        }
        break;
      }
      case 'grid': {
        const rows = Array.isArray(v) ? v : [];
        if (field.min_rows !== undefined && rows.length < field.min_rows) {
          fieldErrors[field.key] = `${field.label}: at least ${field.min_rows} row(s) required`;
        } else if (field.max_rows !== undefined && rows.length > field.max_rows) {
          fieldErrors[field.key] = `${field.label}: at most ${field.max_rows} row(s) allowed`;
        }
        // Per-row required is checked at row-edit time; we don't deep-validate here.
        break;
      }
      default:
        break;
    }
  }

  // 2. Cross-field validation rules
  for (const rule of spec.validations ?? []) {
    const ok = evalBool(rule.rule, values, /*defaultOnError*/ true);
    if (ok) continue;
    if ((rule.severity ?? 'error') === 'error') {
      // Key by rule id so the message is stable and dismissable per rule
      fieldErrors[`__rule_${rule.id}`] = rule.message;
    } else {
      warnings.push({ id: rule.id, message: rule.message });
    }
  }

  return {
    fieldErrors,
    warnings,
    canSave: Object.keys(fieldErrors).length === 0,
  };
}

/** Helper used by single-row grids — same logic, but treating row_fields as the spec.fields. */
export function runRowValidations(
  rowFields: ReadonlyArray<FieldSpec>,
  rowValues: FormValues,
  rowValidations: ReadonlyArray<ValidationRule> = [],
): ValidationResult {
  const pseudoSpec: FormSpec = {
    fields: rowFields as FieldSpec[],
    validations: rowValidations as ValidationRule[],
  };
  return runValidations(pseudoSpec, rowValues);
}
